/**
 * Descobre COMO exibir um arquivo, a partir do que se sabe dele.
 *
 * Fica separado do componente porque a decisão é usada antes de abrir o
 * visualizador — a tela precisa saber se mostra "Visualizar" ou manda direto
 * para download, sem montar o modal para descobrir.
 */

export type PreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'unsupported'

/**
 * Como o conteúdo de texto deve ser editado. A distinção existe porque salvar
 * é destrutivo no formato errado: passar um `.json` pelo editor de texto rico
 * devolveria o arquivo sem indentação e, dependendo do conteúdo, inválido.
 */
export type TextFlavor =
    /** HTML de verdade: o editor rico é o formato nativo. */
    | 'html'
    /** Prosa (.txt, .md): editor rico para escrever, salvo como texto puro. */
    | 'prose'
    /** Estrutura ou código: editor monoespaçado, salvo byte a byte. */
    | 'code'

const IMAGE_EXTENSIONS = [
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif', 'heic',
]
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogv', 'mov', 'm4v', 'mkv']
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'opus']
const HTML_EXTENSIONS = ['html', 'htm']
const PROSE_EXTENSIONS = ['txt', 'md', 'markdown', 'rtf']
const CODE_EXTENSIONS = [
    'json', 'csv', 'tsv', 'xml', 'yml', 'yaml', 'log', 'ini', 'conf', 'env',
    'js', 'mjs', 'ts', 'tsx', 'jsx', 'css', 'scss', 'sql', 'sh', 'py',
]

/** Extensão em minúsculas, sem ponto. Ignora query string de URL assinada. */
export function getExtension(fileName: string): string {
    const withoutQuery = fileName.split('?')[0] ?? ''
    const lastDot = withoutQuery.lastIndexOf('.')
    if (lastDot < 0) return ''
    return withoutQuery.slice(lastDot + 1).toLowerCase()
}

/**
 * O `mimeType` tem prioridade sobre a extensão: ele foi gravado no envio, a
 * partir do que o navegador detectou, enquanto a extensão é só o nome que o
 * usuário deu ao arquivo.
 */
export function resolvePreviewKind(
    fileName: string | undefined,
    mimeType?: string | null,
): PreviewKind {
    const mime = (mimeType ?? '').toLowerCase()

    if (mime.startsWith('image/')) return 'image'
    if (mime.startsWith('video/')) return 'video'
    if (mime.startsWith('audio/')) return 'audio'
    if (mime === 'application/pdf') return 'pdf'
    if (mime.startsWith('text/')) return 'text'
    if (
        mime === 'application/json' ||
        mime === 'application/xml' ||
        mime === 'application/javascript' ||
        mime === 'application/x-yaml'
    ) {
        return 'text'
    }

    const extension = getExtension(fileName ?? '')
    if (!extension) return 'unsupported'

    if (IMAGE_EXTENSIONS.includes(extension)) return 'image'
    if (VIDEO_EXTENSIONS.includes(extension)) return 'video'
    if (AUDIO_EXTENSIONS.includes(extension)) return 'audio'
    if (extension === 'pdf') return 'pdf'
    if (
        HTML_EXTENSIONS.includes(extension) ||
        PROSE_EXTENSIONS.includes(extension) ||
        CODE_EXTENSIONS.includes(extension)
    ) {
        return 'text'
    }

    return 'unsupported'
}

export function resolveTextFlavor(
    fileName: string | undefined,
    mimeType?: string | null,
): TextFlavor {
    const mime = (mimeType ?? '').toLowerCase()
    const extension = getExtension(fileName ?? '')

    if (mime === 'text/html' || HTML_EXTENSIONS.includes(extension)) return 'html'
    if (PROSE_EXTENSIONS.includes(extension)) return 'prose'
    if (CODE_EXTENSIONS.includes(extension)) return 'code'

    // `text/plain` sem extensão reconhecida: prosa é a aposta menos arriscada
    // para leitura, e o editor avisa antes de salvar.
    if (mime === 'text/plain') return 'prose'

    return 'code'
}

/** Um arquivo tem visualização própria, ou só resta baixar? */
export function canPreview(
    fileName: string | undefined,
    mimeType?: string | null,
): boolean {
    return resolvePreviewKind(fileName, mimeType) !== 'unsupported'
}
